import { useId } from 'react';
import type { ExtractionStatus } from '@shared/schema/billData';
import ConfidenceBadge from './ConfidenceBadge';

interface EditableFieldProps {
  label: string;
  value: string;
  status: ExtractionStatus;
  confidence: number;
  evidence?: string | null;
  onChange: (value: string) => void;
  error?: string | null;
  inputMode?: 'text' | 'numeric' | 'decimal' | 'tel';
  hint?: string;
}

/** A labeled, editable field bound to one `ExtractionField<T>` — every extracted value in Review is editable, per spec. */
export default function EditableField({
  label,
  value,
  status,
  confidence,
  evidence,
  onChange,
  error,
  inputMode = 'text',
  hint,
}: EditableFieldProps) {
  const inputId = useId();
  const errorId = useId();
  const evidenceId = useId();

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={inputId} className="text-sm font-medium text-slate-700">
          {label}
        </label>
        <ConfidenceBadge status={status} confidence={confidence} />
      </div>
      <input
        id={inputId}
        type="text"
        inputMode={inputMode}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={Boolean(error)}
        aria-describedby={[error ? errorId : null, evidence ? evidenceId : null].filter(Boolean).join(' ') || undefined}
        className={`rounded-xl border px-4 py-3 text-base text-slate-900 ${
          error ? 'border-red-400 bg-red-50' : status === 'missing' ? 'border-amber-300 bg-amber-50' : 'border-slate-300 bg-white'
        }`}
      />
      {hint && !error && <p className="text-xs text-slate-500">{hint}</p>}
      {error && (
        <p id={errorId} role="alert" className="text-xs font-medium text-red-700">
          {error}
        </p>
      )}
      {evidence && !error && status !== 'user_edited' && (
        <p id={evidenceId} className="text-xs text-slate-400">
          Found: “{evidence}”
        </p>
      )}
    </div>
  );
}
